import { DataResult, State as DataState } from '@progress/kendo-data-query';
import { GridCellProps } from '@progress/kendo-react-grid';
import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router';
import {
  formatPrice,
  showErrorToast,
} from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import { DataLoader } from '../../../../../../common/widgets/DataLoader';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import Grid, { GridCellTd } from '../../../../../../common/widgets/Grid';
import Icons from '../../../../../../common/widgets/Icons';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { config } from '../../../../config';
import { DropdownOptionModel } from '../../../../models/Common';
import { TransactionItemModel } from '../../../../models/Orders';
import { resources } from '../../../../resources/resources';
import { orderService } from '../../../../services/orders';
import {
  EmptyStateBudgetTransactions,
  EmptyStateDashboardBudgetTransactions,
} from '../../../Common/EmptyStates';

interface TransactionsGridProps {
  period?: DropdownOptionModel;
  onPeriodChange?: (period: DropdownOptionModel) => void;
  periodOptions?: DropdownOptionModel[];
  showFiltersAndReport: boolean;
  transactions: {
    data?: TransactionItemModel[];
    total: number;
    dataState: DataState;
    onDataReceived: (data: DataResult) => void;
    onDataStateChange: (dataState: DataState) => void;
  };
  showErrorPanel?: boolean;
  isForDashboard: boolean;
}

interface TransactionsGridState {
  isReportButtonLoading: boolean;
}

export const TransactionsGrid = (props: TransactionsGridProps) => {
  const {
    period,
    onPeriodChange,
    periodOptions,
    showFiltersAndReport,
    showErrorPanel,
    transactions: { data, total, dataState, onDataReceived, onDataStateChange },
    isForDashboard,
  } = props;
  const { t: translate } = useTranslation();
  const [state, setState] = useState<TransactionsGridState>({
    isReportButtonLoading: false,
  });

  const onGenerateReportClick = async () => {
    setState(
      produce(d => {
        d.isReportButtonLoading = true;
      }),
    );
    const resp = await orderService.downloadTransactionsReport(
      translate(resources.orders.transactions),
    );
    setState(
      produce(d => {
        d.isReportButtonLoading = false;
      }),
    );
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.cannotGenerateFile));
    }
  };

  const isPageLoading = !data;

  return (
    <>
      <div
        className={`orders-transactions-filters ${
          !!data && !data.length ? 'no-data' : ''
        }`}
      >
        {showFiltersAndReport && !isPageLoading && !showErrorPanel && (
          <>
            <div className="orders-transactions-selector">
              <label>{translate(resources.orders.choosePeriod)}:</label>
              <Dropdown
                data={periodOptions ?? []}
                value={period}
                onChange={e => onPeriodChange?.(e.value)}
                dataItemKey="value"
                textField="text"
              />
            </div>
            <Button
              onClick={onGenerateReportClick}
              loading={state.isReportButtonLoading}
              icon={<Icons.DownloadFile />}
              disabled={!!data && !data.length}
              className="btn btn-solid btn-sm"
            >
              <span className="btn-text">
                {translate(resources.orders.downloadReport)}
              </span>
            </Button>
          </>
        )}
      </div>
      {!!data && !data.length ? (
        <>
          {isForDashboard ? (
            <EmptyStateDashboardBudgetTransactions />
          ) : (
            <EmptyStateBudgetTransactions />
          )}
        </>
      ) : (
        <>
          <DataLoader
            getData={orderService.getTransactions}
            dataState={dataState}
            onDataReceived={onDataReceived}
          />

          <div className="my-account-grid acc-transactions-grid">
            <Loader
              loading={isPageLoading}
              loader={isForDashboard ? <Skeleton /> : <LoadingSpinner />}
              showErrorPanel={showErrorPanel}
              errorPanelMessage={translate(
                resources.orders.transactionsGenericError,
              )}
            >
              <Grid
                data={data}
                pageable={{
                  pageSizes: config.grid.pageSizes,
                  pageSizeValue: dataState.take,
                  info: false,
                  previousNext: true,
                }}
                {...dataState}
                onDataStateChange={e => onDataStateChange(e.dataState)}
                total={total}
                sortable={{ allowUnsort: true, mode: 'single' }}
                columns={[
                  {
                    title: translate(resources.orders.date),
                    field: 'dateAdded',
                    sortable: true,
                    cell: DateCell,
                  },
                  {
                    field: 'statusName',
                    sortable: false,
                    title: translate(resources.orders.status),
                    cell: StatusNameCell,
                  },
                  {
                    title: translate(resources.orders.description),
                    field: 'description',
                    sortable: false,
                    cell: DescriptionCell,
                  },
                  {
                    title: translate(resources.orders.amount),
                    field: 'amount',
                    sortable: true,
                    cell: AmountCell,
                    headerClassName: 'grid-number',
                  },
                  {
                    cell: ActionsCell,
                    sortable: false,
                    headerClassName: 'grid-actions-cell',
                  },
                ]}
              />
            </Loader>
          </div>
        </>
      )}
    </>
  );
};

const DateCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.dateAdded}</GridCellTd>
);

const StatusNameCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>
    <span
      className="grid-status-badge"
      style={{
        backgroundColor: dataItem.statusBgColor,
        color: dataItem.statusFgColor,
      }}
    >
      {dataItem.statusName}
    </span>
  </GridCellTd>
);

const DescriptionCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd>{dataItem.description}</GridCellTd>
);

const AmountCell = ({ dataItem }: GridCellProps) => (
  <GridCellTd className="grid-number">
    {formatPrice(dataItem.amount)} {dataItem.currencyCode}
  </GridCellTd>
);

const ActionsCell = ({ dataItem }: GridCellProps) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  return (
    <GridCellTd className="grid-actions-cell">
      {dataItem.orderId != 0 && (
        <span className="grid-actions">
          <button
            className="btn-link grid-action-nav"
            onClick={() => navigate(`/my-account/orders/${dataItem.orderId}`)}
          >
            <span className="btn-icon">
              <Icons.ArrowButton />
            </span>
            <span className="btn-text">
              {translate(resources.orders.seeDetails)}
            </span>
          </button>
        </span>
      )}
    </GridCellTd>
  );
};
